import React, { useState, useEffect } from 'react';
import { API_BASE } from '../config/apiConfig';
import IntelligenceChart from './IntelligenceChart';

/**
 * AnalyticsPanel — Renders BUCI-Questions as action buttons grouped by action_type
 * and displays AI responses (text + optional chart_data via IntelligenceChart).
 *
 * Props:
 *   - clientId: string
 *   - productType: string
 *   - clientConfig: object
 *
 * Behavior:
 *   1. On mount: fetch BUCI questions from GET /v1/analytics/buci-questions
 *   2. Group questions by action_type (recommend, execute, monitor, decision, genai, rag, agentic)
 *   3. Render each group as a section with clickable buttons
 *   4. On button click: POST /v1/analytics/execute with question_id, question, action_type
 *   5. Display response — text answer + optional chart_data via IntelligenceChart
 *   6. Show loading state during execution, error state on failure
 *   7. If zero BUCI questions returned, show "No analytics configured" message with guidance
 */

const ACTION_TYPE_META = {
  recommend: { icon: '💡', label: 'Recommendations' },
  execute:   { icon: '⚡', label: 'Execute Actions' },
  monitor:   { icon: '📈', label: 'Monitoring' },
  decision:  { icon: '🎯', label: 'Decisions' },
  genai:     { icon: '🤖', label: 'GenAI Queries' },
  rag:       { icon: '📚', label: 'RAG Intelligence' },
  agentic:   { icon: '🧠', label: 'Agentic AI' },
};

const getAuthToken = () => localStorage.getItem('pnp_access_token') || '';

const AnalyticsPanel = ({ clientId, productType, clientConfig }) => {
  const [questions, setQuestions] = useState([]);
  const [grouped, setGrouped] = useState({});
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [executingId, setExecutingId] = useState(null);
  const [response, setResponse] = useState(null);
  const [executeError, setExecuteError] = useState(null);
  const [lastFailedQuestion, setLastFailedQuestion] = useState(null);

  // Fetch BUCI questions on mount
  useEffect(() => {
    const fetchQuestions = async () => {
      setLoading(true);
      setFetchError(null);
      try {
        const token = getAuthToken();
        const headers = { 'Content-Type': 'application/json' };
        if (token) headers['Authorization'] = `Bearer ${token}`;

        const res = await fetch(
          `${API_BASE}/v1/analytics/buci-questions?client_id=${encodeURIComponent(clientId)}`,
          { headers }
        );

        if (!res.ok) {
          throw new Error(`Failed to fetch analytics questions (${res.status})`);
        }

        const data = await res.json();
        const questionList = data.questions || [];
        setQuestions(questionList);

        // Group by action_type
        const groups = {};
        questionList.forEach((q) => {
          const type = q.action_type || 'genai';
          if (!groups[type]) groups[type] = [];
          groups[type].push(q);
        });
        setGrouped(groups);
      } catch (err) {
        setFetchError(err.message || 'Failed to load analytics questions');
      } finally {
        setLoading(false);
      }
    };

    if (clientId) {
      fetchQuestions();
    }
  }, [clientId]);

  // Execute a BUCI question with 10-second timeout
  const handleExecute = async (question) => {
    setExecutingId(question.question_id);
    setResponse(null);
    setExecuteError(null);
    setLastFailedQuestion(null);

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);

    try {
      const token = getAuthToken();
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const res = await fetch(`${API_BASE}/v1/analytics/execute`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          question_id: question.question_id,
          question: question.question,
          action_type: question.action_type || 'genai',
          client_id: clientId,
          product_type: productType,
        }),
        signal: controller.signal,
      });

      if (!res.ok) {
        throw new Error(`Execution failed (${res.status})`);
      }

      const data = await res.json();
      setResponse(data);
    } catch (err) {
      if (err.name === 'AbortError') {
        setExecuteError('Request timed out. The analytics query took longer than 10 seconds. Please try again or select a different question.');
      } else {
        setExecuteError(err.message || 'Failed to execute analytics query');
      }
      setLastFailedQuestion(question);
    } finally {
      clearTimeout(timeoutId);
      setExecutingId(null);
    }
  };

  // Retry handler for execution errors — re-executes the last failed question
  const handleRetry = () => {
    if (lastFailedQuestion) {
      handleExecute(lastFailedQuestion);
    } else {
      setExecuteError(null);
      setResponse(null);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div style={{ padding: '2rem' }}>
        <h2 style={{ marginBottom: '1rem' }}>📊 Analytics</h2>
        <div style={{
          background: '#f8f9fa', borderRadius: '12px', padding: '2rem',
          textAlign: 'center', color: '#666', border: '1px solid #e0e0e0',
        }}>
          <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>⏳</div>
          <p>Loading analytics questions...</p>
        </div>
      </div>
    );
  }

  // Fetch error state
  if (fetchError) {
    return (
      <div style={{ padding: '2rem' }}>
        <h2 style={{ marginBottom: '1rem' }}>📊 Analytics</h2>
        <div style={{
          background: '#fff3e0', borderRadius: '12px', padding: '2rem',
          textAlign: 'center', color: '#e65100', border: '1px solid #ffcc80',
        }}>
          <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>⚠️</div>
          <p>{fetchError}</p>
          <button
            onClick={() => window.location.reload()}
            style={{
              marginTop: '1rem', padding: '0.5rem 1.5rem', borderRadius: '8px',
              border: 'none', background: '#e65100', color: '#fff',
              fontWeight: 600, cursor: 'pointer',
            }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // No analytics configured state
  if (questions.length === 0) {
    return (
      <div style={{ padding: '2rem' }}>
        <h2 style={{ marginBottom: '1rem' }}>📊 Analytics</h2>
        <div style={{
          background: '#f8f9fa', borderRadius: '12px', padding: '2rem',
          textAlign: 'center', color: '#666', border: '1px solid #e0e0e0',
        }}>
          <div style={{ fontSize: '2rem', marginBottom: '0.75rem' }}>📭</div>
          <h3 style={{ color: '#333', marginBottom: '0.5rem' }}>No analytics configured</h3>
          <p style={{ fontSize: '0.9rem', maxWidth: '400px', margin: '0 auto', lineHeight: 1.6 }}>
            Your product type does not have BUCI questions configured yet.
            Contact your administrator to set up analytics questions for your business vertical.
          </p>
        </div>
      </div>
    );
  }

  // Render grouped BUCI questions
  const actionTypes = Object.keys(grouped);

  return (
    <div style={{ padding: '2rem' }}>
      <h2 style={{ marginBottom: '1.5rem' }}>📊 Analytics</h2>

      {/* BUCI Question Groups */}
      {actionTypes.map((actionType) => {
        const meta = ACTION_TYPE_META[actionType] || { icon: '❓', label: actionType };
        const items = grouped[actionType];

        return (
          <div key={actionType} style={{ marginBottom: '1.5rem' }}>
            <h3 style={{
              fontSize: '0.95rem', fontWeight: 600, color: '#444',
              marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem',
            }}>
              <span>{meta.icon}</span>
              <span>{meta.label}</span>
              <span style={{
                fontSize: '0.75rem', background: '#e3f2fd', color: '#1565c0',
                borderRadius: '10px', padding: '2px 8px', fontWeight: 500,
              }}>
                {items.length}
              </span>
            </h3>

            <div style={{
              display: 'flex', flexWrap: 'wrap', gap: '0.5rem',
            }}>
              {items.map((q) => (
                <button
                  key={q.question_id}
                  onClick={() => handleExecute(q)}
                  disabled={executingId !== null}
                  title={q.question}
                  style={{
                    padding: '0.5rem 1rem',
                    borderRadius: '8px',
                    border: '1px solid #e0e0e0',
                    background: executingId === q.question_id ? '#e3f2fd' : '#fff',
                    color: '#333',
                    fontSize: '0.85rem',
                    cursor: executingId !== null ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s',
                    maxWidth: '300px',
                    textAlign: 'left',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    opacity: executingId !== null && executingId !== q.question_id ? 0.6 : 1,
                  }}
                  aria-label={`Execute: ${q.question}`}
                >
                  {executingId === q.question_id ? '⏳ Running...' : (q.label || q.question)}
                </button>
              ))}
            </div>
          </div>
        );
      })}

      {/* Execution Loading State */}
      {executingId && (
        <div style={{
          background: '#e3f2fd', borderRadius: '12px', padding: '1.5rem',
          textAlign: 'center', color: '#1565c0', border: '1px solid #bbdefb',
          marginTop: '1rem',
        }}>
          <div style={{ fontSize: '1.2rem', marginBottom: '0.5rem' }}>🔄</div>
          <p style={{ fontWeight: 500 }}>Executing analytics query...</p>
        </div>
      )}

      {/* Execution Error State */}
      {executeError && (
        <div style={{
          background: executeError.includes('timed out') ? '#fff3e0' : '#fce4ec',
          borderRadius: '12px', padding: '1.5rem',
          color: executeError.includes('timed out') ? '#e65100' : '#c62828',
          border: `1px solid ${executeError.includes('timed out') ? '#ffcc80' : '#ef9a9a'}`,
          marginTop: '1rem',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
            <span style={{ fontSize: '1.2rem' }}>{executeError.includes('timed out') ? '⏱️' : '❌'}</span>
            <strong>{executeError.includes('timed out') ? 'Request Timed Out' : 'Execution Failed'}</strong>
          </div>
          <p style={{ fontSize: '0.9rem', marginBottom: '0.75rem' }}>{executeError}</p>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              onClick={handleRetry}
              style={{
                padding: '0.4rem 1.2rem', borderRadius: '6px', border: 'none',
                background: executeError.includes('timed out') ? '#e65100' : '#c62828',
                color: '#fff', fontWeight: 600,
                cursor: 'pointer', fontSize: '0.85rem',
              }}
            >
              Retry
            </button>
            <button
              onClick={() => { setExecuteError(null); setLastFailedQuestion(null); }}
              style={{
                padding: '0.4rem 1.2rem', borderRadius: '6px',
                border: '1px solid #ccc', background: '#fff',
                color: '#555', fontWeight: 500,
                cursor: 'pointer', fontSize: '0.85rem',
              }}
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* Response Display */}
      {response && !executingId && (
        <div style={{
          background: '#fff', borderRadius: '12px', padding: '1.5rem',
          border: '1px solid #e0e0e0', marginTop: '1rem',
        }}>
          {/* Text Answer */}
          {response.answer && (
            <div style={{ lineHeight: 1.7, color: '#333', fontSize: '0.95rem' }}>
              {response.answer}
            </div>
          )}

          {/* Chart Data via IntelligenceChart */}
          {response.chart_data && response.chart_data.length > 0 && (
            <IntelligenceChart
              answer={response.answer}
              chartData={response.chart_data}
              chartType="bar"
              isCurrency={false}
            />
          )}

          {/* Source/metadata footer */}
          {(response.source || response.terminology_applied) && (
            <div style={{
              marginTop: '1rem', paddingTop: '0.75rem', borderTop: '1px solid #eee',
              fontSize: '0.75rem', color: '#999', display: 'flex', gap: '1rem',
            }}>
              {response.source && <span>Source: {response.source}</span>}
              {response.terminology_applied && <span>✓ Domain terminology applied</span>}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AnalyticsPanel;
